import { useCallback, useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { api } from "@/lib/api";
import { useLiveRoomEvents } from "@/hooks/useLiveRoomWS";
import {
  Wand2, Sparkles, Gift, Target,
  CircleDot, Swords, Users, Coffee, PieChart, Puzzle, Square, Brain, BarChart3, Megaphone, Dices,
  Check, Coins, Crown, Share2, ChevronDown, ChevronRight, DoorOpen, LayoutDashboard,
} from "lucide-react";

/**
 * LiveRoomCreatorTools — Slice B (Feb 2026): grouped Creator Control Center.
 *
 * Reorganized from the flat 12-tile grid into a scannable stack of grouped
 * sections. Everything a host can do from within their own Live lives
 * here so nothing spills onto the video surface:
 *
 *   1. Live Insights — Coins earned, Top Gifter, Viewers count (tap
 *      opens existing viewers drawer). Data is fetched here so the
 *      on-stage LiveEarningsChip / TopGifterChip can be safely hidden
 *      for the host without losing visibility.
 *   2. Stream — Camera on/off + Flip (portalled in by <HostCamControls>
 *      because those need LiveKit's local-participant context), Pause
 *      (BRB) / Resume.
 *   3. Battle & Collab — CONTEXTUAL "Skip this Battle" while
 *      battle.status === "active" (opens confirmation, hits new
 *      POST /battles/{id}/forfeit — leaver = loser, opponent wins,
 *      Live keeps streaming). Otherwise shows "Start a Battle".
 *      Invite co-host below.
 *   4. Monetization & Fun (collapsed by default) — Wheel / Poll /
 *      Goal / Paid Live / Custom Gifts / Revenue Split / Extensions.
 *   5. Appearance (collapsed by default) — Beauty / Filters.
 *   6. Share stream link (one-tap copy).
 *   7. Sticky End Live pill.
 *
 * Non-hosts do NOT open this sheet — LiveRoom passes `onOpenTools`
 * only for `isHost`, so the wrench button in LiveRoomActionRail
 * doesn't render for viewers/cohosts.
 */
export default function LiveRoomCreatorTools({
  open,
  onOpenChange,
  isHost,
  streamId,
  viewerCount,
  copied,
  caps = {},
  battleActive,
  battleId,
  brbActive,
  onOpenWheel,
  onOpenBattle,
  onOpenCohost,
  onToggleBrb,
  onOpenPoll,
  onOpenGoal,
  onOpenPaidLive,
  onOpenSplit,
  onOpenExtensions,
  onOpenGames,
  onOpenDare,
  onOpenBlast,
  onOpenBeauty,
  onOpenFilters,
  onOpenCustomGifts,
  onOpenViewers,
  onShare,
  onEndStream,
  onOpenAdmin,
  onForfeitOptimistic, // parent may want to snap UI while forfeit is in flight
}) {
  const [monetizationOpen, setMonetizationOpen] = useState(false);
  const [forfeitConfirmOpen, setForfeitConfirmOpen] = useState(false);
  const [forfeitBusy, setForfeitBusy] = useState(false);

  // --- Live Insights data (host-only, fetched only while sheet mounted) --
  const [earnings, setEarnings] = useState(0);
  const [bambooStats, setBambooStats] = useState(null);
  const [topGifter, setTopGifter] = useState(null);
  const loadInsights = useCallback(async () => {
    if (!isHost || !streamId) return;
    try {
      const [e, t] = await Promise.all([
        api.get(`/live/${streamId}/earnings`),
        api.get(`/live/${streamId}/top-gifter?limit=1`),
      ]);
      setEarnings(Number(e?.data?.total_coins) || 0);
      setBambooStats(e?.data?.bamboo_enabled ? { live: Number(e.data.total_bamboo) || 0, lifetime: Number(e.data.lifetime_bamboo) || 0 } : null);
      const entries = t?.data?.entries;
      setTopGifter(Array.isArray(entries) && entries[0] ? entries[0] : null);
    } catch {
      /* silent — chips stay at last known values */
    }
  }, [isHost, streamId]);
  useEffect(() => {
    loadInsights();
    // Only poll while sheet is open — no need to keep a 20s heartbeat
    // running when the wrench is closed (data will refresh on next open).
    if (!open) return undefined;
    const t = setInterval(loadInsights, 20_000);
    return () => clearInterval(t);
  }, [open, loadInsights]);
  useLiveRoomEvents((msg) => {
      if (msg?.channel === "gift:sent" && isHost) {
        const coins = Number(msg?.gift?.creator_coins ?? msg?.gift?.coins ?? msg?.coins ?? 0);
        if (Number.isFinite(coins) && coins > 0) setEarnings((prev) => prev + coins);
        // Top-gifter can change — re-fetch just the top row.
        loadInsights();
      }
  });

  const handleForfeit = useCallback(async () => {
    if (!battleId || forfeitBusy) return;
    setForfeitBusy(true);
    try {
      await api.post(`/battles/${battleId}/forfeit`);
      onForfeitOptimistic?.();
      setForfeitConfirmOpen(false);
      onOpenChange(false);
    } catch (e) {
      // Leave the modal open on failure so the host can retry.
    } finally {
      setForfeitBusy(false);
    }
  }, [battleId, forfeitBusy, onOpenChange, onForfeitOptimistic]);

  if (!isHost) return null;

  const Tile = ({ testId, icon: Icon, label, onClick, disabled }) => (
    <button
      type="button"
      data-testid={testId}
      onClick={() => { if (!disabled) { onClick?.(); onOpenChange(false); } }}
      disabled={disabled}
      className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white transition disabled:opacity-40"
    >
      <Icon size={20} />
      <span className="text-[11px] tracking-wide">{label}</span>
    </button>
  );

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          data-testid="live-creator-tools-panel"
          className="bg-zinc-950 border-t border-white/10 rounded-t-2xl px-4 pb-6 pt-4 text-white max-h-[85vh] overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle className="text-sm font-medium tracking-wide">
              Creator Control Center
            </SheetTitle>
          </SheetHeader>

          {/* 1. Live Insights — read-at-a-glance stats */}
          <section className="mt-3" data-testid="live-tools-insights">
            <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
              <span className="inline-block w-1 h-1 rounded-full bg-yellow-400" />
              Live Insights
            </h3>
            <div className="rounded-xl bg-white/5 border border-white/10 divide-y divide-white/5">
              <div className="flex items-center gap-3 px-3 py-2.5" data-testid="live-tools-insight-earnings">
                <span className="w-8 h-8 rounded-full bg-yellow-400/15 border border-yellow-400/30 flex items-center justify-center">
                  <Coins size={14} className="text-yellow-300" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-zinc-500 uppercase tracking-widest">{bambooStats ? "Bamboo earned this Live" : "Coins earned this Live"}</div>
                  <div className="text-lg font-semibold tabular-nums" data-testid="live-tools-earnings-value">
                    {bambooStats ? <>🎋 {bambooStats.live.toLocaleString()}</> : earnings.toLocaleString()}
                  </div>
                  {bambooStats && <div className="text-[11px] text-zinc-500">from {earnings.toLocaleString()} gifted coins</div>}
                </div>
              </div>
              {bambooStats && (
                <div className="flex items-center gap-3 px-3 py-2.5" data-testid="live-tools-insight-lifetime-bamboo">
                  <span className="w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-sm">🎋</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-zinc-500 uppercase tracking-widest">Lifetime Bamboo earned</div>
                    <div className="text-lg font-semibold tabular-nums" data-testid="live-tools-lifetime-bamboo-value">{bambooStats.lifetime.toLocaleString()}</div>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 px-3 py-2.5" data-testid="live-tools-insight-topgifter">
                <span className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                  <Crown size={14} className="text-amber-300" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-zinc-500 uppercase tracking-widest">Top gifter</div>
                  {topGifter ? (
                    <div className="flex items-center gap-2 mt-0.5">
                      <Avatar className="w-5 h-5 border border-white/15">
                        <AvatarImage src={topGifter.user?.avatar_url} />
                        <AvatarFallback className="bg-amber-600 text-white text-[9px]">
                          {topGifter.user?.display_name?.[0]?.toUpperCase() || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-semibold truncate">
                        {topGifter.user?.display_name}
                      </span>
                      <span className="text-xs text-amber-200 tabular-nums">
                        · {Number(topGifter.coins).toLocaleString()}
                      </span>
                    </div>
                  ) : (
                    <div className="text-sm text-zinc-400">No gifts yet</div>
                  )}
                </div>
              </div>
              <button
                type="button"
                data-testid="live-tools-open-viewers"
                onClick={() => { onOpenViewers?.(); onOpenChange(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/5 transition"
              >
                <span className="w-8 h-8 rounded-full bg-white/10 border border-white/15 flex items-center justify-center">
                  <Users size={14} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-zinc-500 uppercase tracking-widest">Watching now</div>
                  <div className="text-sm font-semibold tabular-nums">
                    {typeof viewerCount === "number" ? viewerCount : 0} viewers
                  </div>
                </div>
                <ChevronRight size={16} className="text-zinc-500" />
              </button>
            </div>
          </section>

          {/* 2. Live Tools — Camera + BRB + Appearance (Slice 1: absorbed
              the old pre-Live wall; editors open LiveToolsPanel). */}
          <section className="mt-4" data-testid="live-tools-stream">
            <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
              <span className="inline-block w-1 h-1 rounded-full bg-rose-400" />
              Live Tools
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Camera on/off + Flip portalled here by HostCamControls */}
              <div id="live-host-cam-slot" data-testid="live-host-cam-slot" className="flex items-center gap-2" />
              {caps.hasBrb && onToggleBrb && (
                <button
                  type="button"
                  data-testid="live-tools-brb"
                  onClick={() => { onToggleBrb(); onOpenChange(false); }}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition ${
                    brbActive
                      ? "bg-amber-500/20 hover:bg-amber-500/30 border-amber-400/40 text-amber-100"
                      : "bg-white/5 hover:bg-white/10 border-white/10 text-white"
                  }`}
                >
                  <Coffee size={20} />
                  <span className="text-[11px] tracking-wide">{brbActive ? "End BRB" : "Pause (BRB)"}</span>
                </button>
              )}
              {caps.hasBeauty && onOpenBeauty && (
                <Tile testId="live-tools-beauty" icon={Wand2} label="Beauty" onClick={onOpenBeauty} />
              )}
              {caps.hasFilters && onOpenFilters && (
                <Tile testId="live-tools-filters" icon={Sparkles} label="Filters" onClick={onOpenFilters} />
              )}
            </div>
          </section>

          {/* 2.5 Games — Trivia + Quick Poll (Slices 2+3) */}
          {onOpenGames && (
            <section className="mt-4" data-testid="live-tools-games">
              <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
                <span className="inline-block w-1 h-1 rounded-full bg-emerald-400" />
                Games
              </h3>
              <div className="grid grid-cols-3 gap-2">
                <Tile testId="live-tools-trivia" icon={Brain} label="Trivia" onClick={() => onOpenGames("trivia")} />
                <Tile testId="live-tools-quickpoll" icon={BarChart3} label="Quick Poll" onClick={() => onOpenGames("poll")} />
                {onOpenDare && (
                  <Tile testId="live-tools-dare" icon={Dices} label="Dare or Shot" onClick={onOpenDare} />
                )}
                {onOpenBlast && (
                  <Tile testId="live-tools-blast" icon={Megaphone} label="Blast" onClick={onOpenBlast} />
                )}
              </div>
            </section>
          )}

          {/* 3. Battle & Collab — contextual */}
          {(caps.hasBattle || caps.hasCohost) && (
            <section className="mt-4" data-testid="live-tools-battle">
              <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
                <span className="inline-block w-1 h-1 rounded-full bg-fuchsia-400" />
                Battle & Collab
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {caps.hasBattle && battleActive && battleId && (
                  <button
                    type="button"
                    data-testid="live-tools-skip-battle"
                    onClick={() => setForfeitConfirmOpen(true)}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-rose-600/20 hover:bg-rose-600/30 border border-rose-500/40 text-rose-100 transition"
                  >
                    <DoorOpen size={20} />
                    <span className="text-[11px] tracking-wide">Skip Battle</span>
                  </button>
                )}
                {caps.hasBattle && !battleActive && onOpenBattle && (
                  <Tile
                    testId="live-tools-battle"
                    icon={Swords}
                    label="Start Battle"
                    onClick={onOpenBattle}
                  />
                )}
                {caps.hasCohost && onOpenCohost && (
                  <Tile
                    testId="live-tools-cohost"
                    icon={Users}
                    label="Invite co-host"
                    onClick={onOpenCohost}
                  />
                )}
              </div>
            </section>
          )}

          {/* 4. Monetization & Fun — collapsible, collapsed by default */}
          <section className="mt-4" data-testid="live-tools-monetization">
            <button
              type="button"
              onClick={() => setMonetizationOpen((s) => !s)}
              className="w-full flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-2 hover:text-zinc-300 transition"
              aria-expanded={monetizationOpen}
              data-testid="live-tools-monetization-toggle"
            >
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-1 h-1 rounded-full bg-emerald-400" />
                Monetization &amp; Fun
              </span>
              {monetizationOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
            {monetizationOpen && (
              <div className="grid grid-cols-3 gap-2">
                {caps.hasWheel && onOpenWheel && <Tile testId="live-tools-wheel" icon={CircleDot} label="Wheel" onClick={onOpenWheel} />}
                {caps.hasGoalBar && onOpenGoal && <Tile testId="live-tools-goal" icon={Target} label="Goal" onClick={onOpenGoal} />}
                {caps.hasCustomGifts && onOpenCustomGifts && <Tile testId="live-tools-custom" icon={Gift} label="Custom Gifts" onClick={onOpenCustomGifts} />}
                {caps.hasRevenueSplit && onOpenSplit && <Tile testId="live-tools-split" icon={PieChart} label="Revenue Split" onClick={onOpenSplit} />}
                {caps.hasExtensions && onOpenExtensions && <Tile testId="live-tools-ext" icon={Puzzle} label="Extensions" onClick={onOpenExtensions} />}
              </div>
            )}
          </section>

          {/* 5. Appearance — merged into Live Tools (Slice 1). */}

          {/* 6. Share */}
          {onShare && (
            <section className="mt-4" data-testid="live-tools-share">
              <button
                type="button"
                data-testid="live-tools-share-btn"
                onClick={() => { onShare(); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white transition"
              >
                <span className="w-8 h-8 rounded-full bg-white/10 border border-white/15 flex items-center justify-center">
                  {copied ? <Check size={14} className="text-emerald-400" /> : <Share2 size={14} />}
                </span>
                <span className="text-sm font-medium">
                  {copied ? "Link copied" : "Share stream link"}
                </span>
              </button>
            </section>
          )}

          {onOpenAdmin && (
            <section className="mt-3" data-testid="live-tools-admin">
              <button
                type="button"
                data-testid="live-tools-admin-btn"
                onClick={() => { onOpenAdmin(); onOpenChange(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-rose-500/30 text-white transition"
              >
                <span className="w-8 h-8 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-300 flex items-center justify-center">
                  <LayoutDashboard size={14} />
                </span>
                <span className="text-sm font-medium">Admin Dashboard</span>
                <span className="ml-auto text-[10px] text-emerald-300">Live keeps running</span>
              </button>
            </section>
          )}

          {/* 7. Sticky End Live */}
          {onEndStream && (
            <button
              type="button"
              data-testid="live-tools-end"
              onClick={() => { onEndStream(); onOpenChange(false); }}
              className="mt-5 w-full rounded-full bg-rose-600 hover:bg-rose-500 text-white text-xs uppercase tracking-widest py-2.5 flex items-center justify-center gap-2"
            >
              <Square size={12} /> End Live
            </button>
          )}
        </SheetContent>
      </Sheet>

      {/* Forfeit confirmation — kept OUT of the sheet so closing the sheet
          doesn't unmount the dialog mid-decision. */}
      <AlertDialog open={forfeitConfirmOpen} onOpenChange={setForfeitConfirmOpen}>
        <AlertDialogContent
          data-testid="skip-battle-confirm"
          className="bg-zinc-950 border border-white/10 text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Leave this battle?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Your opponent will win. Your Live will continue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="skip-battle-cancel"
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              Keep battling
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="skip-battle-confirm-btn"
              onClick={handleForfeit}
              disabled={forfeitBusy}
              className="bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-60"
            >
              {forfeitBusy ? "Leaving…" : "Leave battle"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
